import { Feature, FeatureCollection, Geometry, GeoJsonProperties, MultiPolygon, Point, Polygon, Position } from 'geojson';
import union from '@turf/union';
import flatten from '@turf/flatten';
import bbox from '@turf/bbox';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import difference from '@turf/difference';

export type FC = FeatureCollection<Geometry, GeoJsonProperties>;
export type F = Feature<Geometry, GeoJsonProperties>;

export function buildSimulationMask(simAreas: F[]): F | null {
  if (!simAreas?.length) return null;
  return simAreas.reduce<F | null>((acc, sa) => {
    try {
      return acc ? (union(acc as any, sa as any) as any) : (sa as any);
    } catch {
      return acc ?? (sa as any);
    }
  }, null);
}

export function featureFitsMask(feat: F, mask: F): boolean {
  try {
    const parts = (flatten(feat).features || [feat]) as F[];

    return parts.every((part) => {
      switch (part.geometry.type) {
        case 'Point':
          return booleanPointInPolygon(part as Feature<Point>, mask as Feature<Polygon | MultiPolygon>, {
            ignoreBoundary: false,
          });
        case 'MultiPoint':
          return getGeometryCoordinates(part.geometry).every((coordinate) =>
            booleanPointInPolygon(coordinate as Position, mask as Feature<Polygon | MultiPolygon>, {
              ignoreBoundary: false,
            })
          );
        case 'LineString':
        case 'MultiLineString':
          return getGeometryCoordinates(part.geometry).every((coordinate) =>
            booleanPointInPolygon(coordinate as Position, mask as Feature<Polygon | MultiPolygon>, {
              ignoreBoundary: false,
            })
          );
        case 'Polygon':
        case 'MultiPolygon':
          return polygonFitsMask(part, mask);
        default:
          return false;
      }
    });
  } catch {
    return false;
  }
}

export function fcFitsMask(fc: FC, mask: F): boolean {
  return fc.features.every((f) => featureFitsMask(f as F, mask));
}

export function toFeatureCollection(input: FC | F): FC {
  return input?.type === 'FeatureCollection'
    ? (input as FC)
    : { type: 'FeatureCollection', features: [input as F] };
}

export function extentOf(input: FC | F): ReturnType<typeof bbox> {
  return bbox(input) as ReturnType<typeof bbox>;
}

function getGeometryCoordinates(geometry: Geometry): number[][] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates as number[]];
    case 'MultiPoint':
    case 'LineString':
      return geometry.coordinates as number[][];
    case 'MultiLineString':
    case 'Polygon':
      return (geometry.coordinates as number[][][]).flat();
    case 'MultiPolygon':
      return (geometry.coordinates as number[][][][]).flat(2);
    default:
      return [];
  }
}

function polygonFitsMask(polygonFeature: F, mask: F): boolean {
  const polygonCoordinates = getGeometryCoordinates(polygonFeature.geometry);
  return polygonCoordinates.every((coordinate) =>
    booleanPointInPolygon(coordinate as Position, mask as Feature<Polygon | MultiPolygon>, {
      ignoreBoundary: false,
    })
  );
}
